import * as React from 'react';
import { forwardRef, type ReactElement } from 'react';

import classnames from 'classnames';

import { type SizeType, useSize } from '../../features/config';
import { ButtonBase, type ButtonBaseProps, ButtonContent } from '../Button';
import { Spinner, type SpinnerProps } from '../Spinner';

import styles from './IconButton.module.scss';

export type IconButtonShape = 'default' | 'square' | 'circle';

type BaseProps = {
    size?: SizeType;
    shape?: IconButtonShape;
    icon?: ReactElement;
    slotProps?: {
        spinnerProps?: Partial<SpinnerProps>;
    };
};

export type IconButtonProps = Omit<ButtonBaseProps, keyof BaseProps> & BaseProps;

export const IconButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, IconButtonProps>(
    (
        {
            shape = 'default',
            size: sizeProp,
            className,
            loading,
            children,
            icon,
            slotProps,
            ...props
        }: IconButtonProps,
        ref
    ) => {
        const size = useSize(sizeProp, 'large', 'IconButton');
        const iconNode = children ?? icon;
        const { spinnerProps } = slotProps ?? {};
        return (
            <ButtonBase
                ref={ref}
                loading={loading}
                className={classnames(styles.base, styles[shape], styles[size], className)}
                size={size}
                {...(props as ButtonBaseProps)}
            >
                <ButtonContent className={styles.content}>
                    {loading ? (
                        <Spinner size={size === 'large' ? 24 : 16} {...spinnerProps} />
                    ) : (
                        iconNode
                    )}
                </ButtonContent>
            </ButtonBase>
        );
    }
);

IconButton.displayName = 'IconButton';
